import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import api, { formatError, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { LogOut, Trophy, CalendarDays, MapPin, Clock, Trash2, Plus, User as UserIcon, Pencil, Copy } from "lucide-react";
import { format } from "date-fns";
import { el, enUS } from "date-fns/locale";
import { Link } from "react-router-dom";

const FALLBACK_LOGO = "https://images.unsplash.com/photo-1737027616012-a7e5b17c8ae4?crop=entropy&cs=srgb&fm=jpg&q=85";

export function formatTime12h(hhmm) {
    if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return hhmm || "";
    const [hStr, mStr] = hhmm.split(":");
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    const period = h >= 12 ? "PM" : "AM";
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

export function formatDmy(ymd) {
    if (!ymd || typeof ymd !== "string") return ymd || "";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m) return ymd;
    return `${m[3]}-${m[2]}-${m[1]}`;
}

export function isMapUrl(s) {
    if (!s || typeof s !== "string") return false;
    return /^(https?:\/\/|www\.)/i.test(s.trim());
}

function normalizeMapUrl(s) {
    const v = (s || "").trim();
    return v.startsWith("www.") ? `https://${v}` : v;
}

function openMapUrl(rawUrl) {
    const url = normalizeMapUrl(rawUrl);
    if (!url) return;
    // Primary: open in new tab
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (win) {
        try { win.opener = null; } catch (e) { /* noop */ }
        return;
    }
    // Fallback when popups are blocked: synthesize a target="_blank" anchor click.
    // This avoids ever replacing the current/top window with the external URL.
    try {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
    } catch (e) { /* swallow – we never want to navigate the current tab */ }
}

const URL_RE = /\b((?:https?:\/\/|www\.)\S+)/i;

export function extractUrl(s) {
    if (!s || typeof s !== "string") return null;
    const m = URL_RE.exec(s);
    return m ? m[1].replace(/[),.;]+$/g, "") : null;
}

export function titleWithoutUrl(s) {
    if (!s || typeof s !== "string") return "";
    return s.replace(URL_RE, "").replace(/\s+/g, " ").trim();
}

const SPORTS = [
    "Football",
    "Basketball",
    "Volleyball",
    "Beach Volleyball",
    "Tennis",
    "Table Tennis",
    "Cricket",
    "Badminton",
    "Boxing",
    "Golf",
    "Water Sports",
    "Motorsport",
];

function Header({ user, onLogout }) {
    const { t } = useLanguage();
    const token = localStorage.getItem("token");
    const avatarUrl = user.avatar_path ? `${API_BASE}/files/${user.avatar_path}?auth=${token}` : null;
    return (
        <header className="glass sticky top-0 z-20 border-b border-border">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1 min-w-0 flex-shrink">
                    <img src="https://customer-assets.emergentagent.com/job_schedule-sync-109/artifacts/0i7pk9q1_refpairlogo_trans.png" alt="RefPair" className="w-12 h-12 object-contain flex-shrink-0" />
                    <div className="min-w-0">
                        <div className="font-heading text-sm font-semibold leading-tight truncate">RefPair<span className="text-muted-foreground font-normal text-[0.78em] tracking-tighter -ml-[1px]">.com</span></div>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <LanguageSwitcher variant="compact" />
                    <Link to="/profile" className="flex items-center gap-3 px-2 py-1 rounded-md hover:bg-accent/40 transition-colors" data-testid="user-profile-link">
                        <div className="w-9 h-9 rounded-full bg-accent border border-border overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="user-header-avatar">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                                <UserIcon className="w-4 h-4 text-muted-foreground" />
                            )}
                        </div>
                        <div className="text-right hidden md:block">
                            <div className="text-sm font-medium truncate max-w-[160px]" data-testid="user-display-name">{user.name}</div>
                            <div className="label-mono text-[10px] truncate max-w-[160px]">{user.email}</div>
                        </div>
                    </Link>
                    <Button variant="outline" size="sm" onClick={onLogout} className="rounded-md flex-shrink-0" data-testid="user-logout-btn">
                        <LogOut className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{t("common.signOut")}</span>
                    </Button>
                </div>
            </div>
        </header>
    );
}

export default function UserDashboard() {
    const { user, logout } = useAuth();
    const { t, lang } = useLanguage();
    const dateLocale = lang === "el" ? el : enUS;
    const [date, setDate] = useState();
    const [selectedTournament, setSelectedTournament] = useState("");
    const [workOption, setWorkOption] = useState("All day");
    const [startTime, setStartTime] = useState("09:00");
    const [endTime, setEndTime] = useState("11:00");
    const [notes, setNotes] = useState("");
    const [suggestedPrice, setSuggestedPrice] = useState("");
    const [availability, setAvailability] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const [saving, setSaving] = useState(false);
    const [tournamentDialog, setTournamentDialog] = useState({ open: false, items: [] });
    const [errors, setErrors] = useState({});

    const verifiedAt = useMemo(() => (user.verified_at ? new Date(user.verified_at) : null), [user.verified_at]);
    const minDate = useMemo(() => {
        if (!verifiedAt) return new Date();
        const d = new Date(verifiedAt);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [verifiedAt]);

    const loadData = async () => {
        try {
            const [a, t] = await Promise.all([api.get("/availability"), api.get("/tournaments")]);
            setAvailability(a.data);
            setTournaments(t.data);
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const saveSlot = async () => {
        if (!date) {
            toast.error(t("dashboard.pickDateFirst"));
            return;
        }
        const nextErrors = {
            selectedTournament: !selectedTournament.trim(),
            workOption: !workOption,
            notes: !notes.trim(),
            suggestedPrice: !suggestedPrice.trim(),
        };
        setErrors(nextErrors);
        if (Object.values(nextErrors).some(Boolean)) {
            toast.error(t("common.requiredFields"));
            return;
        }
        setSaving(true);
        try {
            const body = {
                date: format(date, "yyyy-MM-dd"),
                selected_tournament: selectedTournament.trim(),
                work_option: workOption,
                notes: notes.trim(),
                SuggestedPricePH: suggestedPrice.trim(),
            };
            await api.post("/availability", body);
            toast.success(t("dashboard.availabilityAdded"));
            setNotes("");
            setSuggestedPrice("");
            setSelectedTournament("");
            setErrors({});
            await loadData();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const removeSlot = async (id) => {
        try {
            await api.delete(`/availability/${id}`);
            toast.success(t("dashboard.removed"));
            setAvailability((prev) => prev.filter((a) => a.id !== id));
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        }
    };

    const sortedAvailability = useMemo(() => {
        return [...availability].sort((a, b) => {
            if (a.date !== b.date) return a.date < b.date ? 1 : -1;
            return (a.start_time || "") < (b.start_time || "") ? 1 : -1;
        });
    }, [availability]);

    const selectedTournamentSet = useMemo(() => {
        return new Set(
            selectedTournament
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
        );
    }, [selectedTournament]);

    const isTournamentSelected = (name) => selectedTournamentSet.has((name || "").trim());

    const toggleTournamentSelected = (name, on) => {
        const clean = (name || "").trim();
        if (!clean) return;
        const set = new Set(selectedTournamentSet);
        if (on) set.add(clean); else set.delete(clean);
        const next = Array.from(set).join(", ");
        setSelectedTournament(next);
        if (next && errors.selectedTournament) setErrors((p) => ({ ...p, selectedTournament: false }));
    };

    const selectedDays = useMemo(() => availability.map((a) => new Date(`${a.date}T00:00:00`)), [availability]);

    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const tournamentDays = useMemo(() => {
        const days = [];
        for (const t of tournaments) {
            if (!t.start_date) continue;
            const start = new Date(`${t.start_date}T00:00:00`);
            const end = t.end_date ? new Date(`${t.end_date}T00:00:00`) : start;
            const cur = new Date(start);
            while (cur <= end) {
                if (cur >= today) days.push(new Date(cur));
                cur.setDate(cur.getDate() + 1);
            }
        }
        return days;
    }, [tournaments, today]);

    const pastTournamentDays = useMemo(() => {
        const days = [];
        for (const t of tournaments) {
            if (!t.start_date) continue;
            const start = new Date(`${t.start_date}T00:00:00`);
            const end = t.end_date ? new Date(`${t.end_date}T00:00:00`) : start;
            const cur = new Date(start);
            while (cur <= end) {
                if (cur < today) days.push(new Date(cur));
                cur.setDate(cur.getDate() + 1);
            }
        }
        return days;
    }, [tournaments, today]);

    const todayYmd = useMemo(() => format(today, "yyyy-MM-dd"), [today]);

    const upcomingTournaments = useMemo(() => {
        return tournaments.filter((t) => {
            const end = t.end_date || t.start_date || "";
            // Keep tournaments that are still running or yet to start
            return end >= todayYmd;
        });
    }, [tournaments, todayYmd]);

    return (
        <div className="min-h-screen bg-background">
            <Header user={user} onLogout={logout} />
            <main className="max-w-7xl mx-auto px-8 py-10 space-y-10">
                <section>
                    <div className="label-mono mb-2">{t("dashboard.heading")}</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{t("dashboard.hello", { name: user.name })}</h1>
                    <p className="text-muted-foreground mt-1">
                        {t("dashboard.markAvailability")}
                    </p>
                </section>

                <section className="grid lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-3 bg-white border border-border rounded-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-heading text-lg font-medium">{t("dashboard.eventCalendar")}</h2>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-accent inline-block" /> {t("dashboard.legendYourSlots")}</span>
                                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" /> {t("dashboard.legendTournaments")}</span>
                            </div>
                        </div>
                        <div className="flex flex-col lg:flex-row gap-6">
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={(d) => {
                                    setDate(d);
                                    if (!d) return;
                                    const ymd = format(d, "yyyy-MM-dd");
                                    const matches = tournaments.filter((t) => {
                                        const s = t.start_date || "";
                                        const e = t.end_date || s;
                                        // Only show tournament details for current/future tournaments
                                        const ends = e || s;
                                        if (ends && ends < todayYmd) return false;
                                        return s && ymd >= s && ymd <= e;
                                    });
                                    if (matches.length > 0) {
                                        setSelectedTournament("");
                                        setTournamentDialog({ open: true, items: matches });
                                    }
                                }}
                                weekStartsOn={1}
                                disabled={(d) => {
                                    if (d < minDate) return true;
                                    // Past tournament dates: visible (light-blue circle) but not selectable.
                                    const dayStart = new Date(d);
                                    dayStart.setHours(0, 0, 0, 0);
                                    if (dayStart < today) {
                                        const ymd = format(dayStart, "yyyy-MM-dd");
                                        return tournaments.some((t) => {
                                            const s = t.start_date || "";
                                            const e = t.end_date || s;
                                            return s && ymd >= s && ymd <= e;
                                        });
                                    }
                                    return false;
                                }}
                                modifiers={{ booked: selectedDays, tournament: tournamentDays, pastTournament: pastTournamentDays }}
                                modifiersClassNames={{
                                    booked: "bg-accent text-primary-foreground font-semibold",
                                    tournament: "!bg-blue-600 !text-white !rounded-full font-semibold hover:!bg-blue-700",
                                    pastTournament: "!bg-blue-200 !text-blue-700 !rounded-full font-semibold !opacity-100 hover:!bg-blue-200 cursor-not-allowed",
                                }}
                                className="rounded-md border border-border"
                                data-testid="availability-calendar"
                            />
                            <div className="flex-1 space-y-4 min-w-[240px]">
                                <div>
                                    <div className="label-mono mb-2">{t("dashboard.selectedDate")}</div>
                                    <div className="mono text-2xl font-medium" data-testid="selected-date-display">
                                        {date ? format(date, "EEE, dd MMM yyyy", { locale: dateLocale }) : "—"}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="selected-tournament">{t("dashboard.selectedTournamentLabel")}<span className="text-destructive ml-0.5" aria-hidden="true">*</span></Label>
                                    <Input id="selected-tournament" type="text" maxLength={200} readOnly
                                        value={selectedTournament}
                                        placeholder={t("dashboard.selectedTournamentPlaceholder")}
                                        aria-invalid={errors.selectedTournament ? "true" : "false"}
                                        className={`h-11 rounded-md cursor-default ${errors.selectedTournament ? "bg-red-50 border-red-300 focus-visible:ring-red-400" : ""}`}
                                        data-testid="availability-tournament-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="work-option">{t("dashboard.workOptionLabel")}<span className="text-destructive ml-0.5" aria-hidden="true">*</span></Label>
                                    <Select value={workOption} onValueChange={(v) => { setWorkOption(v); if (errors.workOption) setErrors((p) => ({ ...p, workOption: false })); }}>
                                        <SelectTrigger id="work-option"
                                            aria-invalid={errors.workOption ? "true" : "false"}
                                            className={`h-11 rounded-md ${errors.workOption ? "bg-red-50 border-red-300 focus:ring-red-400" : ""}`}
                                            data-testid="availability-work-option-select">
                                            <SelectValue placeholder="Select an option" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Morning" data-testid="work-option-morning">{t("dashboard.workMorning")}</SelectItem>
                                            <SelectItem value="Afternoon" data-testid="work-option-afternoon">{t("dashboard.workAfternoon")}</SelectItem>
                                            <SelectItem value="All day" data-testid="work-option-all">{t("dashboard.workAllDay")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="comment">{t("dashboard.commentLabel")}<span className="text-destructive ml-0.5" aria-hidden="true">*</span></Label>
                                    <Textarea id="comment" value={notes}
                                        onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors((p) => ({ ...p, notes: false })); }}
                                        rows={3} maxLength={1000}
                                        placeholder={t("dashboard.commentPlaceholder")}
                                        aria-invalid={errors.notes ? "true" : "false"}
                                        className={`rounded-md min-h-[80px] ${errors.notes ? "bg-red-50 border-red-300 focus-visible:ring-red-400" : ""}`}
                                        data-testid="availability-comment-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="suggested-price">{t("dashboard.suggestedPriceLabel")}<span className="text-destructive ml-0.5" aria-hidden="true">*</span></Label>
                                    <Input id="suggested-price" type="text" maxLength={100}
                                        value={suggestedPrice}
                                        onChange={(e) => { setSuggestedPrice(e.target.value); if (errors.suggestedPrice) setErrors((p) => ({ ...p, suggestedPrice: false })); }}
                                        placeholder={t("dashboard.suggestedPricePlaceholder")}
                                        aria-invalid={errors.suggestedPrice ? "true" : "false"}
                                        className={`h-11 rounded-md mono ${errors.suggestedPrice ? "bg-red-50 border-red-300 focus-visible:ring-red-400" : ""}`}
                                        data-testid="availability-suggested-price-input" />
                                </div>
                                <Button onClick={saveSlot} disabled={saving}
                                    className="w-full h-11 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                                    data-testid="availability-save-btn">
                                    <Plus className="w-4 h-4 mr-1" /> {saving ? t("dashboard.saving") : t("dashboard.addAvailability")}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2 bg-white border border-border rounded-md p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-heading text-lg font-medium">{t("dashboard.yourAvailability")}</h2>
                            <div className="label-mono mono" data-testid="availability-count">{t("dashboard.slotsCount", { n: availability.length })}</div>
                        </div>
                        {availability.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-10 text-center" data-testid="availability-empty">
                                {t("dashboard.noSlots")}
                            </p>
                        ) : (
                            <ul className="divide-y divide-border stagger-in" data-testid="availability-list">
                                {sortedAvailability.map((a) => (
                                    <li key={a.id} className="py-3 flex items-center justify-between" data-testid={`availability-item-${a.id}`}>
                                        <div>
                                            <div className="mono text-sm font-medium">
                                                {format(new Date(`${a.date}T00:00:00`), "dd MMM yyyy", { locale: dateLocale })}{a.work_option ? ` · ${a.work_option}` : ""}{a.start_time ? ` · ${a.start_time}${a.end_time ? `–${a.end_time}` : ""}` : ""}
                                            </div>
                                            {a.selected_tournament && <div className="text-xs text-primary mt-0.5">{a.selected_tournament}</div>}
                                            {a.SuggestedPricePH && (
                                                <div className="text-xs mono mt-0.5" data-testid={`availability-price-${a.id}`}>
                                                    {a.SuggestedPricePH}
                                                </div>
                                            )}
                                            {a.notes && <div className="text-xs text-muted-foreground mt-0.5">{a.notes}</div>}
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => removeSlot(a.id)}
                                            data-testid={`availability-delete-${a.id}`} className="text-muted-foreground hover:text-destructive">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                <section>
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="label-mono mb-1">{t("dashboard.upcoming")}</div>
                            <h2 className="font-heading text-2xl font-semibold">{t("dashboard.tournaments")}</h2>
                        </div>
                        <div className="label-mono mono" data-testid="tournament-count">{t("dashboard.activeCount", { n: upcomingTournaments.length })}</div>
                    </div>
                    {upcomingTournaments.length === 0 ? (
                        <div className="bg-white border border-border rounded-md p-10 text-center text-muted-foreground" data-testid="tournaments-empty">
                            {t("dashboard.noTournaments")}
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in" data-testid="tournament-list">
                            {upcomingTournaments.map((t) => (
                                <TournamentCard key={t.id} tournament={t} />
                            ))}
                        </div>
                    )}
                </section>
            </main>

            <Dialog open={tournamentDialog.open} onOpenChange={(o) => setTournamentDialog({ ...tournamentDialog, open: o })}>
                <DialogContent className="sm:max-w-2xl rounded-md max-h-[85vh] overflow-y-auto" data-testid="tournament-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {t("dashboard.tournamentsOn", { date: date ? format(date, "EEE, dd MMM yyyy", { locale: dateLocale }) : "" })}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2" data-testid="tournament-detail-list">
                        {tournamentDialog.items.map((tour) => {
                            const token = localStorage.getItem("token");
                            const logoSrc = tour.logo_path ? `${API_BASE}/files/${tour.logo_path}?auth=${token}` : FALLBACK_LOGO;
                            return (
                                <div key={tour.id} className="border border-border rounded-md p-4 flex gap-4" data-testid={`tournament-detail-${tour.id}`}>
                                    <div className="w-24 h-24 rounded-md bg-accent/40 overflow-hidden flex-shrink-0">
                                        <img src={logoSrc} alt={tour.name} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.src = FALLBACK_LOGO; }} />
                                    </div>
                                    <div className="flex-1 space-y-1.5">
                                        <div className="flex items-start justify-between gap-3">
                                            <h3 className="font-heading text-lg font-semibold leading-tight">{tour.name}</h3>
                                            <div className="flex items-center gap-3 flex-shrink-0" data-testid={`tournament-detail-select-${tour.id}`}>
                                                <span className={`mono text-sm font-semibold tracking-wide ${isTournamentSelected(tour.name) ? "text-primary" : "text-muted-foreground"}`}>
                                                    {isTournamentSelected(tour.name) ? t("common.yes") : t("common.no")}
                                                </span>
                                                <Switch
                                                    checked={isTournamentSelected(tour.name)}
                                                    onCheckedChange={(v) => toggleTournamentSelected(tour.name, v)}
                                                    className="h-7 w-12 [&>span]:h-6 [&>span]:w-6 [&>span]:data-[state=checked]:translate-x-5"
                                                    data-testid={`tournament-detail-toggle-${tour.id}`}
                                                />
                                            </div>
                                        </div>
                                        {tour.tournament_url && (
                                            <a
                                                href={normalizeMapUrl(tour.tournament_url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-700 hover:underline text-sm inline-flex items-center gap-1 break-all"
                                                data-testid={`tournament-detail-url-link-${tour.id}`}
                                            >
                                                {t("common.eventWebsite")}
                                            </a>
                                        )}
                                        {tour.sport_type && (
                                            <div className="text-sm text-muted-foreground">
                                                <div>{tour.sport_type}</div>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <CalendarDays className="w-4 h-4" />
                                            <span className="mono">{formatDmy(tour.start_date)}{tour.end_date && tour.end_date !== tour.start_date ? ` → ${formatDmy(tour.end_date)}` : ""}</span>
                                        </div>
                                        {(tour.event_location || tour.place) && (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <MapPin className="w-4 h-4 flex-shrink-0" />
                                                {isMapUrl(tour.event_location || tour.place) ? (
                                                    <a
                                                        href={normalizeMapUrl(tour.event_location || tour.place)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline"
                                                        data-testid={`tournament-detail-map-link-${tour.id}`}
                                                    >
                                                        {t("dashboard.viewOnMap")}
                                                    </a>
                                                ) : (
                                                    <span>{tour.event_location || tour.place}</span>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex flex-wrap gap-2 pt-1">
                                            {tour.age_categories && <span className="label-mono px-2 py-0.5 rounded-md bg-accent/60 text-[10px]">{t("dashboard.ageLabel")} {tour.age_categories}</span>}
                                            {tour.format_categories && <span className="label-mono px-2 py-0.5 rounded-md bg-accent/60 text-[10px]">{tour.format_categories}</span>}
                                        </div>
                                        {tour.other_details && (
                                            <p className="text-sm text-muted-foreground whitespace-pre-wrap pt-2">{tour.other_details}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TimePicker12h({ value, onChange, testid }) {
    // value: "HH:MM" (24h). Display as 12h with AM/PM.
    const parse = (v) => {
        const [hStr = "0", mStr = "0"] = (v || "").split(":");
        const h24 = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        const period = h24 >= 12 ? "PM" : "AM";
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        return { h12, m, period };
    };
    const { h12, m, period } = parse(value);

    const toString24 = (h12v, mv, pv) => {
        let h = parseInt(h12v, 10) || 12;
        if (h < 1) h = 1;
        if (h > 12) h = 12;
        let mm = parseInt(mv, 10);
        if (Number.isNaN(mm) || mm < 0) mm = 0;
        if (mm > 59) mm = 59;
        let h24 = h % 12;
        if (pv === "PM") h24 += 12;
        return `${String(h24).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    };

    const onHour = (e) => onChange(toString24(e.target.value, m, period));
    const onMin = (e) => onChange(toString24(h12, e.target.value, period));
    const onPeriod = (p) => onChange(toString24(h12, m, p));

    return (
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-stretch">
            <Input type="number" min="1" max="12" value={h12} onChange={onHour}
                className="h-11 rounded-md mono text-center" placeholder="HH"
                data-testid={`${testid}-hour`} />
            <Input type="number" min="0" max="59" value={String(m).padStart(2, "0")} onChange={onMin}
                className="h-11 rounded-md mono text-center" placeholder="MM"
                data-testid={`${testid}-minute`} />
            <Select value={period} onValueChange={onPeriod}>
                <SelectTrigger className="h-11 rounded-md w-[88px] mono" data-testid={`${testid}-period`}>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="AM" data-testid={`${testid}-period-am`}>AM</SelectItem>
                    <SelectItem value="PM" data-testid={`${testid}-period-pm`}>PM</SelectItem>
                </SelectContent>
            </Select>
        </div>
    );
}


export function TournamentCard({ tournament: t, onDelete, onEdit, onClone }) {
    const { t: tr } = useLanguage();
    const token = localStorage.getItem("token");
    const logoSrc = t.logo_path ? `${API_BASE}/files/${t.logo_path}?auth=${token}` : FALLBACK_LOGO;
    return (
        <div className="bg-white border border-border rounded-md overflow-hidden hover-lift" data-testid={`tournament-card-${t.id}`}>
            <div className="aspect-[16/9] bg-accent/40 relative overflow-hidden">
                <img src={logoSrc} alt={t.name} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.src = FALLBACK_LOGO; }} />
            </div>
            <div className="p-5 space-y-3">
                <h3 className="font-heading text-lg font-semibold leading-tight">{t.name}</h3>
                {t.tournament_url && (
                    <a
                        href={normalizeMapUrl(t.tournament_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 hover:underline text-sm inline-flex items-center gap-1 break-all"
                        data-testid={`tournament-card-url-link-${t.id}`}
                    >
                        {tr("common.eventWebsite")}
                    </a>
                )}
                {t.sport_type && (
                    <div className="text-sm text-muted-foreground">
                        <div>{t.sport_type}</div>
                    </div>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CalendarDays className="w-4 h-4" />
                    <span className="mono">{formatDmy(t.start_date)}{t.end_date && t.end_date !== t.start_date ? ` → ${formatDmy(t.end_date)}` : ""}</span>
                </div>
                {(t.event_location || t.place) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        {isMapUrl(t.event_location || t.place) ? (
                            <a
                                href={normalizeMapUrl(t.event_location || t.place)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                                data-testid={`tournament-card-map-link-${t.id}`}
                            >
                                {tr("dashboard.viewOnMap")}
                            </a>
                        ) : (
                            <span>{t.event_location || t.place}</span>
                        )}
                    </div>
                )}
                {t.age_categories && (
                    <div className="text-sm text-muted-foreground">
                        <div className="label-mono">{tr("dashboard.ageLabel")}:</div>
                        <div>{t.age_categories}</div>
                    </div>
                )}
                {t.format_categories && (
                    <div className="text-sm text-muted-foreground">
                        <div className="label-mono">{tr("dashboard.format")}:</div>
                        <div>{t.format_categories}</div>
                    </div>
                )}
                {t.other_details && (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{t.other_details}</p>
                )}
                {t.age_group && !t.age_categories && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{tr("dashboard.ageGroup")}: <span className="mono">{t.age_group}</span></span>
                    </div>
                )}
                {(onEdit || onDelete || onClone) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                        {onEdit && (
                            <Button variant="outline" size="sm" onClick={() => onEdit(t)} className="flex-1 min-w-[90px]" data-testid={`tournament-edit-${t.id}`}>
                                <Pencil className="w-3.5 h-3.5 mr-1" /> {tr("common.edit")}
                            </Button>
                        )}
                        {onClone && (
                            <Button variant="outline" size="sm" onClick={() => onClone(t)} className="flex-1 min-w-[90px]" data-testid={`tournament-clone-${t.id}`}>
                                <Copy className="w-3.5 h-3.5 mr-1" /> {tr("common.clone")}
                            </Button>
                        )}
                        {onDelete && (
                            <Button variant="outline" size="sm" onClick={() => onDelete(t.id)} className="flex-1 min-w-[90px]" data-testid={`tournament-delete-${t.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> {tr("common.trash")}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
